import {z} from 'zod'

import {apiFetch} from '../api/fetch'
import {
    type CreateProjectBillingTermsInput,
    createProjectBillingTermsRequestSchema,
} from '../projects/schemas'
import {
    type ChangePreview,
    changePreviewSchema,
    type CreateServiceScheduleInput,
    createServiceScheduleRequestSchema,
    type ScheduleJob,
    scheduleJobSchema,
    type ServiceSchedule,
    type ServiceScheduleChangeInput,
    serviceScheduleChangeRequestSchema,
    serviceScheduleSchema,
} from './schemas'

export async function createServiceSchedule(
    input: CreateServiceScheduleInput,
): Promise<ServiceSchedule> {
    const body = createServiceScheduleRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/service-schedules', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return serviceScheduleSchema.parse(raw)
}

export async function listServiceSchedules(projectId: string): Promise<ServiceSchedule[]> {
    const raw = await apiFetch<unknown>(`/service-schedules?project_id=${projectId}`)
    return z.array(serviceScheduleSchema).parse(raw)
}

export async function getServiceSchedule(scheduleId: string): Promise<ServiceSchedule> {
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}`)
    return serviceScheduleSchema.parse(raw)
}

export async function reviseServiceScheduleBillingTerms(
    scheduleId: string,
    input: CreateProjectBillingTermsInput,
): Promise<ServiceSchedule> {
    const body = createProjectBillingTermsRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}/billing-terms`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return serviceScheduleSchema.parse(raw)
}

export async function previewServiceScheduleChange(
    scheduleId: string,
    input: ServiceScheduleChangeInput,
): Promise<ChangePreview> {
    const body = serviceScheduleChangeRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}/preview`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return changePreviewSchema.parse(raw)
}

export async function applyServiceScheduleChange(
    scheduleId: string,
    input: ServiceScheduleChangeInput,
): Promise<ServiceSchedule> {
    const body = serviceScheduleChangeRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}/apply`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return serviceScheduleSchema.parse(raw)
}

export async function skipScheduleOccurrence(
    scheduleId: string,
    jobId: string,
): Promise<ScheduleJob> {
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}/jobs/${jobId}/skip`, {
        method: 'POST',
    })
    return scheduleJobSchema.parse(raw)
}

export async function restoreScheduleOccurrence(
    scheduleId: string,
    jobId: string,
): Promise<ScheduleJob> {
    const raw = await apiFetch<unknown>(`/service-schedules/${scheduleId}/jobs/${jobId}/restore`, {
        method: 'POST',
    })
    return scheduleJobSchema.parse(raw)
}
